import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { createTruckAdmin } from '../../store/trucks';

const TRUCK_TYPES = {
  'daycab_1_axle': 'Daycab 1 Axle',
  'daycab_2_axle': 'Daycab 2 Axle',
  'sleeper_1_axle': 'Sleeper 1 Axle',
  'sleeper_2_axle': 'Sleeper 2 Axle',
  'boxtruck': 'Boxtruck'
};

function AddTruckPage() {
  const dispatch = useDispatch();
  const history = useHistory();
  const sessionUser = useSelector(state => state.session.user);
  const accounts = useSelector(state => Object.values(state.accounts));

  const [formData, setFormData] = useState({
    truck_number: '',
    truck_location: '',
    license_plate: '',
    license_state: '',
    type: '',
    year: '',
    make: '',
    model: '',
    red_tagged: false,
    yellow_tagged: false,
    in_shop: false,
    last_inspection: new Date().toISOString().split('T')[0],
    next_inspection: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    last_maintenance: new Date().toISOString().split('T')[0],
    next_maintenance: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    tagged_description: '',
    account_id: sessionUser?.is_admin ? '' : sessionUser?.account_id
  });

  const [errors, setErrors] = useState([]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle checkbox inputs
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
      return;
    }

    // Define fields that should be uppercase
    const uppercaseFields = ['license_plate', 'license_state', 'truck_location', 'truck_number'];

    // Handle different input fields
    const processValue = {
      'year': () => {
        if (!/^\d*$/.test(value)) return formData.year;
        const yearNum = parseInt(value);
        if (yearNum > new Date().getFullYear()) return formData.year;
        return value.slice(0, 4);
      },
      'default': () => uppercaseFields.includes(name) ? value.toUpperCase() : value
    };

    const newValue = (processValue[name] || processValue.default)();

    setFormData(prev => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = [];

    // Basic Information validations
    if (!formData.truck_number) validationErrors.push('Truck number is required');
    if (!formData.truck_location) validationErrors.push('Truck location is required');
    if (!formData.license_plate) validationErrors.push('License plate is required');
    if (!formData.license_state) validationErrors.push('License state is required');
    if (formData.license_state.length !== 2) validationErrors.push('License state must be 2 characters');
    if (sessionUser?.is_admin && !formData.account_id) validationErrors.push('Account is required');

    // Vehicle Details validations
    if (!formData.type) validationErrors.push('Truck type is required');
    if (!formData.year) validationErrors.push('Year is required');
    if (formData.year && (formData.year < 1900 || formData.year > new Date().getFullYear() + 1)) {
      validationErrors.push('Year must be between 1900 and next year');
    }
    if (!formData.make) validationErrors.push('Make is required');
    if (!formData.model) validationErrors.push('Model is required');

    // Tagged Description validation
    if ((formData.red_tagged || formData.yellow_tagged) && !formData.tagged_description) {
      validationErrors.push('Tagged description is required when truck is tagged');
    }

    // Date validations
    const today = new Date().toISOString().split('T')[0];
    if (formData.last_inspection > today) {
      validationErrors.push('Last inspection date cannot be in the future');
    }
    if (formData.last_maintenance > today) {
      validationErrors.push('Last maintenance date cannot be in the future');
    }
    if (formData.next_inspection < today) {
      validationErrors.push('Next inspection date must be in the future');
    }
    if (formData.next_maintenance < today) {
      validationErrors.push('Next maintenance date must be in the future');
    }

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const result = await dispatch(createTruckAdmin(formData));
      if (result) {
        history.push('/');
      }
    } catch (error) {
      setErrors([error.toString()]);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <h1 className="text-8xl font-bold mb-5 text-center text-blue-600">Add New Truck</h1>

        {errors.length > 0 && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <ul className="list-disc list-inside">
              {errors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white shadow-lg rounded-lg p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Left Column - Basic Info */}
            <div className="space-y-6">
              <h2 className="text-4xl font-semibold mb-6 text-gray-800">Basic Information</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-2xl font-medium text-gray-700 mb-2">
                    Truck Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="truck_number"
                    value={formData.truck_number}
                    onChange={handleChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-2xl font-medium text-gray-700 mb-2">
                    Location <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="truck_location"
                    value={formData.truck_location}
                    onChange={handleChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-2xl font-medium text-gray-700 mb-2">
                    License Plate <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="license_plate"
                    value={formData.license_plate}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>

                <div>
                  <label className="block text-2xl font-medium text-gray-700 mb-2">
                    License State <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="license_state"
                    value={formData.license_state}
                    onChange={handleChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {sessionUser?.is_admin && (
                  <div>
                    <label className="block text-2xl font-medium text-gray-700 mb-2">
                      Account <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="account_id"
                      value={formData.account_id}
                      onChange={handleChange}
                      required
                      className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Account</option>
                      {accounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.account_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Vehicle Details */}
            <div className="space-y-6">
              <h2 className="text-4xl font-semibold mb-6 text-gray-800">Vehicle Details</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-2xl font-medium text-gray-700 mb-2">
                    Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Type</option>
                    {Object.entries(TRUCK_TYPES).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-2xl font-medium text-gray-700 mb-2">
                    Year <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="year"
                    value={formData.year}
                    onChange={handleChange}
                    min="1900"
                    max={new Date().getFullYear()}
                    className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-2xl font-medium text-gray-700 mb-2">
                    Make <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="make"
                    value={formData.make}
                    onChange={handleChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-2xl font-medium text-gray-700 mb-2">
                    Model <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Status Section */}
          <div className="mt-8 p-6 bg-gray-50 rounded-lg">
            <h2 className="text-4xl font-semibold mb-6 text-gray-800">Status</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="red_tagged"
                  checked={formData.red_tagged}
                  onChange={handleChange}
                  className="h-6 w-6 text-blue-600"
                />
                <span className="text-2xl text-gray-700">Red Tagged</span>
              </label>

              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="yellow_tagged"
                  checked={formData.yellow_tagged}
                  onChange={handleChange}
                  className="h-6 w-6 text-blue-600"
                />
                <span className="text-2xl text-gray-700">Yellow Tagged</span>
              </label>

              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="in_shop"
                  checked={formData.in_shop}
                  onChange={handleChange}
                  className="h-6 w-6 text-blue-600"
                />
                <span className="text-2xl text-gray-700">In Shop</span>
              </label>
            </div>

            <div className="mt-6">
              <label className="block text-2xl font-medium text-gray-700 mb-2">
                Tagged Description
              </label>
              <textarea
                name="tagged_description"
                value={formData.tagged_description}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows="4"
              />
            </div>
          </div>

          {/* Bottom Buttons */}
          <div className="flex justify-end gap-4 mt-8 pt-6 border-t">
            <button
              type="button"
              onClick={() => history.goBack()}
              className="px-6 py-3 text-2xl text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-all duration-200 w-1/4"
            >
              Cancel
            </button>
            <button id = 'add-truck-button'
              type="submit"
              className="px-6 py-3 text-2xl text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all duration-200 w-1/4"
            >
              Add Truck
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddTruckPage;
